import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaFormsV1Service, serviceName } from '../service';
import { SearchPatientForKioskIO, ServiceQueries } from '../types';
import { useFormsV1Queries } from './use-forms-v1-queries';
import { UseFormsV1QueryEndpointArgs, useFormsV1Query } from './use-forms-v1-query';

/**
 * A hook that returns a query for the `SearchPatientForKiosk` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useSearchPatientForKioskQuery = <E = unknown, D = SearchPatientForKioskIO['output']>(
  args: UseFormsV1QueryEndpointArgs<'SearchPatientForKiosk', E, D>
) =>
  useFormsV1Query({
    endpointName: 'SearchPatientForKiosk',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `SearchPatientForKiosk` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useSearchPatientForKioskQueries = <E = unknown, D = SearchPatientForKioskIO['output']>(
  argsArr: UseFormsV1QueryEndpointArgs<'SearchPatientForKiosk', E, D>[]
) =>
  useFormsV1Queries(
    argsArr.map((args) => ({
      endpointName: 'SearchPatientForKiosk',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `SearchPatientForKiosk` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchSearchPatientForKiosk = () => {
  const queryClient = useQueryClient();

  const fetchSearchPatientForKiosk = async <E = unknown, D = SearchPatientForKioskIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseFormsV1QueryEndpointArgs<'SearchPatientForKiosk', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      SearchPatientForKioskIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'SearchPatientForKiosk'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        SearchPatientForKioskIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'SearchPatientForKiosk'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'SearchPatientForKiosk'>({
          serviceName,
          endpointName: 'SearchPatientForKiosk',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaFormsV1Service.SearchPatientForKiosk(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchSearchPatientForKiosk;
};
